import {
  ChevronDown,
  ChevronRight,
  Globe,
  LogOut,
  Monitor,
  Settings,
  Shield,
  User,
} from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { AuthUser } from "../../types";
import type { TenantApp } from "../../state/auth-store";
import { useShellLocaleStore } from "../../state/shell-locale-store";
import { ShellLanguageSwitcher } from "./ShellLanguageSwitcher";
import { ShellThemeSwitcher } from "./ShellThemeSwitcher";
import { useShellThemeStore } from "../../state/shell-theme-store";

interface ShellUserDropdownProps {
  adminApp?: TenantApp;
  onLogout: () => void;
  user?: AuthUser;
}

export const ShellUserDropdown = ({
  adminApp,
  onLogout,
  user,
}: ShellUserDropdownProps) => {
  const { t } = useTranslation("layout");
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [activeView, setActiveView] = useState<"main" | "theme" | "language">(
    "main",
  );
  const panelId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const locale = useShellLocaleStore((state) => state.locale);
  const themePreference = useShellThemeStore((state) => state.preference);

  useEffect(() => {
    if (!showUserMenu) {
      setActiveView("main");
    }
  }, [showUserMenu]);

  const closeMenu = useCallback(() => {
    setShowUserMenu(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  }, []);

  useEffect(() => {
    if (!showUserMenu) {
      return;
    }

    const focusFirstControl = window.requestAnimationFrame(() => {
      panelRef.current
        ?.querySelector<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        )
        ?.focus();
    });
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeMenu();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFirstControl);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeView, closeMenu, showUserMenu]);

  return (
    <div className="relative shrink-0">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-controls={panelId}
        aria-expanded={showUserMenu}
        onClick={() => setShowUserMenu((current) => !current)}
        className="shell-user-menu-button flex items-center gap-2 rounded-xl px-3 py-2 text-left transition-colors hover:bg-surface-secondary"
      >
        <div className="shell-user-avatar flex h-8 w-8 items-center justify-center rounded-full bg-primary/20">
          <span className="shell-user-initial text-sm font-medium text-chrome-content">
            {user?.name?.charAt(0).toUpperCase()}
          </span>
        </div>
        <div className="shell-user-summary min-w-0 flex-1">
          <p className="shell-user-name">{user?.name}</p>
          <p className="shell-user-role">
            {user?.role ? t(`roles.${user.role}`, { defaultValue: user.role }) : null}
          </p>
        </div>
        <ChevronDown className="shell-user-chevron h-4 w-4 text-content-muted" />
      </button>

      {showUserMenu ? (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={closeMenu}
            aria-hidden="true"
          />
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-label={t("topbar.userMenu.label")}
            className="absolute right-0 top-full z-20 mt-2 w-80 rounded-lg border border-border-subtle bg-surface-primary shadow-floating"
          >
            {activeView === "main" ? (
              <>
                <div className="border-b border-border-subtle p-3">
                  <p className="text-sm font-medium text-content-primary">{user?.name}</p>
                  <p className="text-xs text-content-muted">{user?.email}</p>
                </div>
                <div className="py-1">
                  <Link
                    to="/settings/profile"
                    className="flex items-center gap-3 px-4 py-2 text-sm text-content-secondary transition-colors hover:bg-surface-secondary hover:text-content-primary"
                    onClick={closeMenu}
                  >
                    <User className="h-4 w-4" />
                    {t("topbar.userMenu.profile")}
                  </Link>
                  <Link
                    to="/settings"
                    className="flex items-center gap-3 px-4 py-2 text-sm text-content-secondary transition-colors hover:bg-surface-secondary hover:text-content-primary"
                    onClick={closeMenu}
                  >
                    <Settings className="h-4 w-4" />
                    {t("topbar.userMenu.settings")}
                  </Link>
                  {adminApp ? (
                    <Link
                      to={adminApp.url}
                      className="flex items-center gap-3 px-4 py-2 text-sm text-content-secondary transition-colors hover:bg-surface-secondary hover:text-content-primary"
                      onClick={closeMenu}
                    >
                      <Shield className="h-4 w-4" />
                      {t("topbar.userMenu.administration")}
                    </Link>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setActiveView("theme")}
                    className="flex w-full items-center gap-3 px-4 py-2 text-sm text-content-secondary transition-colors hover:bg-surface-secondary hover:text-content-primary"
                  >
                    <Monitor className="h-4 w-4" />
                    <span>{t("topbar.theme.label")}</span>
                    <span className="ml-auto text-xs font-medium text-content-muted">
                      {t(`topbar.theme.options.${themePreference}.label`)}
                    </span>
                    <ChevronRight className="h-4 w-4 text-content-muted" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveView("language")}
                    className="flex w-full items-center gap-3 px-4 py-2 text-sm text-content-secondary transition-colors hover:bg-surface-secondary hover:text-content-primary"
                  >
                    <Globe className="h-4 w-4" />
                    <span>{t("topbar.language.label")}</span>
                    <span className="ml-auto text-xs font-medium text-content-muted">
                      {t(`topbar.language.options.${locale}.label`)}
                    </span>
                    <ChevronRight className="h-4 w-4 text-content-muted" />
                  </button>
                </div>
                <div className="border-t border-border-subtle py-1">
                  <button
                    onClick={() => {
                      closeMenu();
                      onLogout();
                    }}
                    className="flex w-full items-center gap-3 px-4 py-2 text-sm text-state-danger transition-colors hover:bg-state-dangerSoft"
                  >
                    <LogOut className="h-4 w-4" />
                    {t("topbar.userMenu.logout")}
                  </button>
                </div>
              </>
            ) : activeView === "theme" ? (
              <ShellThemeSwitcher
                onBack={() => setActiveView("main")}
                onClose={closeMenu}
              />
            ) : (
              <ShellLanguageSwitcher
                onBack={() => setActiveView("main")}
                onClose={closeMenu}
              />
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};
